import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, Check, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const BRAND = {
  turquoise: '#81CBD6',
  pink: '#F39DB2',
  yellow: '#FFD16F',
};

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const passwordValidation = {
    minLength: password.length >= 6,
    hasNumber: /\d/.test(password),
    hasLetter: /[a-zA-Z]/.test(password),
    matches: password === confirmPassword && password.length > 0,
  };

  const isValid = Object.values(passwordValidation).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      toast.error('Ungültiger Reset-Link');
      return;
    }

    if (!isValid) {
      toast.error('Bitte prüfen Sie die Passwort-Anforderungen');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Passwort konnte nicht geändert werden');
      }

      setSuccess(true);
      toast.success('Passwort erfolgreich geändert!');
      
      // Redirect after 2 seconds
      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div 
            className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'rgba(239,68,68,0.2)' }}
          >
            <Lock className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Ungültiger Link</h1>
          <p className="text-gray-400 mb-6">
            Der Reset-Link ist ungültig oder abgelaufen. Bitte fordern Sie einen neuen Link an.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 rounded-xl font-bold text-white"
            style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}
          >
            Zur Startseite
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div 
            className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${BRAND.turquoise}20` }}
          >
            <Check className="w-8 h-8" style={{ color: BRAND.turquoise }} />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Passwort geändert!</h1>
          <p className="text-gray-400">
            Ihr Passwort wurde erfolgreich aktualisiert. Sie werden zur Anmeldung weitergeleitet...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_37cca134-41a5-49ca-9252-6c0a7dd12fc1/artifacts/rsel26pf_Fancy_Donuts_Wortmarke.png" 
            alt="Fancy Donuts" 
            className="h-10 mx-auto mb-6"
          />
          <h1 className="text-2xl font-bold text-white mb-2">Neues Passwort erstellen</h1>
          <p className="text-gray-400">Geben Sie Ihr neues Passwort ein</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Password Input */}
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Neues Passwort"
              className="w-full p-4 pr-12 rounded-xl outline-none text-white"
              style={{ 
                backgroundColor: 'rgba(255,255,255,0.05)', 
                border: '1px solid rgba(255,255,255,0.1)' 
              }}
              data-testid="new-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          {/* Confirm Password Input */}
          <div className="relative">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Passwort bestätigen"
              className="w-full p-4 pr-12 rounded-xl outline-none text-white"
              style={{ 
                backgroundColor: 'rgba(255,255,255,0.05)', 
                border: '1px solid rgba(255,255,255,0.1)' 
              }}
              data-testid="confirm-password-input"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
            >
              {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          {/* Password Requirements */}
          <div className="p-4 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
            <p className="text-sm text-gray-400 mb-3">Passwort-Anforderungen:</p>
            <div className="space-y-2">
              <RequirementItem met={passwordValidation.minLength} text="Mindestens 6 Zeichen" />
              <RequirementItem met={passwordValidation.hasLetter} text="Mindestens ein Buchstabe" />
              <RequirementItem met={passwordValidation.hasNumber} text="Mindestens eine Zahl" />
              <RequirementItem met={passwordValidation.matches} text="Passwörter stimmen überein" />
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!isValid || isSubmitting}
            className="w-full py-4 rounded-xl font-bold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}
            data-testid="reset-password-submit"
          >
            {isSubmitting ? 'Wird geändert...' : 'Passwort ändern'}
          </button>

          {/* Back Link */}
          <button
            type="button"
            onClick={() => navigate('/')}
            className="w-full py-3 rounded-xl font-medium text-gray-400 hover:text-white flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Startseite
          </button>
        </form>
      </div>
    </div>
  );
}

function RequirementItem({ met, text }) {
  return (
    <div className="flex items-center gap-2">
      <div 
        className="w-5 h-5 rounded-full flex items-center justify-center"
        style={{ backgroundColor: met ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.1)' }}
      >
        {met && <Check className="w-3 h-3 text-green-500" />}
      </div>
      <span className={`text-sm ${met ? 'text-green-500' : 'text-gray-500'}`}>{text}</span>
    </div>
  );
}
